import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Row, Col, Card, Table, Button, Input, notification,
  Modal, Select, Typography, Tabs, Drawer, Form, Space,
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  DeleteOutlined, EditOutlined, PlusOutlined,
  ExclamationCircleOutlined, SearchOutlined,
  SafetyOutlined, IdcardOutlined,
} from "@ant-design/icons";
import { hasPermission } from "../../components/permissions";

const { confirm } = Modal;
const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;
const { TabPane } = Tabs;
const { Item } = Form;

// ─── Role List ────────────────────────────────────────────────────────────────
function RoleSection() {
  const [list, setList]             = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit]           = useState(10);
  const [sortField, setSortField]   = useState(null);
  const [sortOrder, setSortOrder]   = useState(null);
  const [search, setSearch]         = useState("");

  useEffect(() => { fetchList(); }, []);

  async function fetchList(page = 1, lim = 10, sf = null, so = null, q = "") {
    try {
      setCurrentPage(page);
      setLimit(lim);
      const sort = sf && so ? `&sortField=${sf}&sortOrder=${so}` : "";
      const srch = q ? `&search=${q}` : "";
      const res = await axios.get(
        `${baseUrl}/role/list?page=${page}&limit=${lim}${sort}${srch}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
      );
      if (res.data.status) {
        setList(res.data.result);
        setPagination({ current: page, pageSize: lim, total: res.data.totalCount });
      } else {
        notification.info({ message: "Info", description: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.info({ message: "Info", description: e.response?.data?.message, placement: "topRight" });
    }
  }

  async function handleDelete(id) {
    try {
      const res = await axios.delete(`${baseUrl}/role/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (res.status === 200) {
        fetchList(pagination.current, pagination.pageSize, sortField, sortOrder, search);
        notification.success({ message: "Success", description: "Role deleted!", placement: "topRight" });
      }
    } catch (e) {
      notification.info({ message: "Info", description: e.response?.data?.message, placement: "topRight" });
    }
  }

  const showDeleteConfirm = (id) =>
    confirm({
      title: "Delete this role?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes", okType: "danger", cancelText: "No",
      onOk() { handleDelete(id); },
    });

  const columns = [
    { title: "#", dataIndex: "key", key: "key", width: 60 },
    { title: "Name", dataIndex: "name", key: "name", sorter: true },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Status", dataIndex: "status", key: "status",
      render: (s) => <span style={{ color: s === "Active" ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>{s}</span>,
    },
    {
      title: "Action", key: "action", width: 100,
      render: (_, record) => (
        <div className="button-container">
          {hasPermission("/role", "editPermission") && (
            <Link to={`/role/update/${record.id}`} className="update-btn"><EditOutlined /></Link>
          )}
          {hasPermission("/role", "deletePermission") && (
            <button onClick={() => showDeleteConfirm(record.id)} className="delete-btn"><DeleteOutlined /></button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Table
      title={() => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span style={{ marginRight: 10 }}>Per page:</span>
            <Select defaultValue={10} onChange={(v) => fetchList(1, v, sortField, sortOrder, search)}>
              {[10, 20, 30, 40].map((v) => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </div>
          <Search
            placeholder="Search by name or description"
            onSearch={(v) => { setSearch(v); fetchList(currentPage, limit, sortField, sortOrder, v); }}
            enterButton={<SearchOutlined />}
            style={{ width: 280 }}
          />
        </div>
      )}
      columns={columns}
      dataSource={list.map((r, i) => ({
        key: limit * (currentPage - 1) + i + 1,
        id: r.id,
        name: r.name,
        description: r.description,
        status: r.status,
      }))}
      pagination={{ ...pagination, showTotal: (t) => `Total ${t} records`, style: { marginRight: 20 } }}
      onChange={(p, _f, s) => {
        const so = s.order === "ascend" ? "asc" : "desc";
        setSortField(s.field); setSortOrder(so);
        fetchList(p.current, p.pageSize, s.field, so, search);
      }}
      className="ant-border-space"
      bordered
    />
  );
}

// ─── Staff / Employee List + Drawer ───────────────────────────────────────────
function StaffSection({ onAddClick }) {
  const [list, setList]             = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit]           = useState(10);
  const [sortField, setSortField]   = useState(null);
  const [sortOrder, setSortOrder]   = useState(null);
  const [search, setSearch]         = useState("");

  // Drawer state
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editId, setEditId]           = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [roleList, setRoleList]       = useState([]);
  const [form]                        = Form.useForm();

  useEffect(() => { fetchList(); fetchRoleList(); }, []);

  async function fetchRoleList() {
    try {
      const res = await axios.get(`${baseUrl}/role/listAll`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (res.status === 200) setRoleList(res.data.result);
    } catch (e) { /* silent */ }
  }

  async function fetchList(page = 1, lim = 10, sf = null, so = null, q = "") {
    try {
      setCurrentPage(page);
      setLimit(lim);
      const sort = sf && so ? `&sortField=${sf}&sortOrder=${so}` : "";
      const srch = q ? `&search=${q}` : "";
      const res = await axios.get(
        `${baseUrl}/staff/list?page=${page}&limit=${lim}${sort}${srch}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
      );
      if (res.data.status) {
        setList(res.data.result);
        setPagination({ current: page, pageSize: lim, total: res.data.totalCount });
      } else {
        notification.info({ message: "Info", description: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.info({ message: "Info", description: e.response?.data?.message, placement: "topRight" });
    }
  }

  async function handleDelete(id) {
    try {
      const res = await axios.delete(`${baseUrl}/staff/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (res.status === 200) {
        fetchList(pagination.current, pagination.pageSize, sortField, sortOrder, search);
        notification.success({ message: "Success", description: "Employee deleted!", placement: "topRight" });
      }
    } catch (e) {
      notification.info({ message: "Info", description: e.response?.data?.message, placement: "topRight" });
    }
  }

  const showDeleteConfirm = (id) =>
    confirm({
      title: "Delete this employee?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes", okType: "danger", cancelText: "No",
      onOk() { handleDelete(id); },
    });

  // ── Drawer open/close ──
  const openAdd = () => {
    setEditId(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = async (id) => {
    setEditId(id);
    form.resetFields();
    setDrawerOpen(true);
    try {
      const res = await axios.get(`${baseUrl}/staff/read/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (res.status === 200) form.setFieldsValue(res.data.result);
    } catch (e) {
      notification.info({ message: "Info", description: e.response?.data?.message, placement: "topRight" });
    }
  };

  const closeDrawer = () => { setDrawerOpen(false); setEditId(null); form.resetFields(); };

  const onFinish = async (values) => {
    setDrawerLoading(true);
    try {
      if (editId) {
        await axios.patch(`${baseUrl}/staff/update/${editId}`, values, {
          headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        });
        notification.success({ message: "Success", description: "Employee updated!", placement: "topRight" });
      } else {
        await axios.post(`${baseUrl}/staff/create`, values, {
          headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        });
        notification.success({ message: "Success", description: "Employee added!", placement: "topRight" });
      }
      closeDrawer();
      fetchList(currentPage, limit, sortField, sortOrder, search);
    } catch (e) {
      notification.info({ message: "Info", description: e.response?.data?.message, placement: "topRight" });
    } finally {
      setDrawerLoading(false);
    }
  };

  const columns = [
    { title: "#", dataIndex: "key", key: "key", width: 60 },
    { title: "First Name", dataIndex: "firstName", key: "firstName", sorter: true },
    { title: "Last Name",  dataIndex: "lastName",  key: "lastName",  sorter: true },
    {
      title: "Role", dataIndex: "roleName", key: "roleName",
      render: (name) => <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>,
    },
    { title: "Email",  dataIndex: "email",   key: "email"   },
    { title: "Phone",  dataIndex: "phoneNo", key: "phoneNo" },
    {
      title: "Status", dataIndex: "status", key: "status",
      render: (s) => <span style={{ color: s === "Active" ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>{s}</span>,
    },
    {
      title: "Action", key: "action", width: 100,
      render: (_, record) => (
        <div className="button-container">
          {hasPermission("/staff", "editPermission") && (
            <button className="update-btn" onClick={() => openEdit(record.id)}><EditOutlined /></button>
          )}
          {hasPermission("/staff", "deletePermission") && (
            <button className="delete-btn" onClick={() => showDeleteConfirm(record.id)}><DeleteOutlined /></button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Add button passed up so parent card shows it */}
      {typeof onAddClick === "function" ? null : null}

      <Table
        title={() => (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ marginRight: 10 }}>Per page:</span>
              <Select defaultValue={10} onChange={(v) => fetchList(1, v, sortField, sortOrder, search)}>
                {[10, 20, 30, 40].map((v) => <Option key={v} value={v}>{v}</Option>)}
              </Select>
            </div>
            <Space>
              {hasPermission("/staff", "createPermission") && (
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Employee</Button>
              )}
              <Search
                placeholder="Search by name or email"
                onSearch={(v) => { setSearch(v); fetchList(currentPage, limit, sortField, sortOrder, v); }}
                enterButton={<SearchOutlined />}
                style={{ width: 260 }}
              />
            </Space>
          </div>
        )}
        columns={columns}
        dataSource={list.map((s, i) => ({
          key: limit * (currentPage - 1) + i + 1,
          id: s.id,
          firstName: s.firstName,
          lastName:  s.lastName,
          roleName:  s.roleName,
          email:     s.email,
          phoneNo:   s.phoneNo,
          status:    s.status,
        }))}
        pagination={{ ...pagination, showTotal: (t) => `Total ${t} records`, style: { marginRight: 20 } }}
        onChange={(p, _f, s) => {
          const so = s.order === "ascend" ? "asc" : "desc";
          setSortField(s.field); setSortOrder(so);
          fetchList(p.current, p.pageSize, s.field, so, search);
        }}
        className="ant-border-space"
        bordered
        scroll={{ x: "max-content" }}
      />

      {/* ── Add / Edit Drawer ── */}
      <Drawer
        title={editId ? "Edit Employee" : "Add Employee"}
        width={560}
        open={drawerOpen}
        onClose={closeDrawer}
        bodyStyle={{ paddingBottom: 80 }}
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" loading={drawerLoading} onClick={() => form.submit()}>
              {editId ? "Update" : "Save"}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Item label="First Name" name="firstName" rules={[{ required: true, message: "Enter first name" }]}>
                <Input placeholder="First name" />
              </Item>
            </Col>
            <Col xs={24} sm={12}>
              <Item label="Last Name" name="lastName">
                <Input placeholder="Last name" />
              </Item>
            </Col>
            <Col xs={24} sm={12}>
              <Item label="Phone Number" name="phoneNo" rules={[{ required: true, message: "Enter phone number" }]}>
                <Input placeholder="Phone number" />
              </Item>
            </Col>
            <Col xs={24} sm={12}>
              <Item label="Email" name="email" rules={[{ required: true, message: "Enter email" }]}>
                <Input placeholder="Email" />
              </Item>
            </Col>
            {!editId && (
              <Col xs={24} sm={12}>
                <Item label="Password" name="password" rules={[{ required: true, message: "Enter password" }]}>
                  <Input.Password placeholder="Password" />
                </Item>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <Item label="Role" name="roleId" rules={[{ required: true, message: "Select role" }]}>
                <Select placeholder="Select role">
                  {roleList.map((r) => <Option key={r.id} value={r.id}>{r.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col xs={24} sm={12}>
              <Item label="Status" name="status" rules={[{ required: true, message: "Select status" }]}>
                <Select placeholder="Select status">
                  <Option value="Active">Active</Option>
                  <Option value="Inactive">Inactive</Option>
                </Select>
              </Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
}

// ─── Combined Page ────────────────────────────────────────────────────────────
function StaffRoleList() {
  const [activeTab, setActiveTab] = useState("staff");

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Employee & Role Management</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              activeTab === "role" && hasPermission("/role", "createPermission") && (
                <Link className="custom-btn" to="/role/add">Add Role</Link>
              )
            }
          >
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              style={{ padding: "0 20px" }}
              tabBarStyle={{ marginBottom: 16 }}
            >
              <TabPane tab={<span><IdcardOutlined /> Employee</span>} key="staff">
                {hasPermission("/staff", "readPermission")
                  ? <StaffSection />
                  : <p style={{ padding: 20, color: "#999" }}>No permission to view employees.</p>
                }
              </TabPane>

              <TabPane tab={<span><SafetyOutlined /> Roles</span>} key="role">
                {hasPermission("/role", "readPermission")
                  ? <RoleSection />
                  : <p style={{ padding: 20, color: "#999" }}>No permission to view roles.</p>
                }
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default StaffRoleList;
